import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { COL_ROLES } from '../../../config/collections'
import { formatPermissionsDisplay, normalizePermissions, PERMISSION_KEYS, permissionLabel } from '../../../config/permissions'
import { loadRolesDirectory } from '../../../lib/pocketbaseEmployees'
import { pb } from '../../../pocketbase'
import { useAuth } from '../../../context/AuthContext'
import { matchesListSearch } from '../../../lib/listSearch'
import type { RoleRecord } from '../../../types/records'
import { PageHeader, PageShell, Alert, ButtonLink, SearchField } from '../../../components/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  btn,
  cn,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'

export function RolesPage() {
  const { can } = useAuth()
  const [items, setItems] = useState<RoleRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await loadRolesDirectory()
      setItems(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function removeRow(id: string) {
    if (!window.confirm('Удалить роль? Убедитесь, что ни у кого не осталась эта роль.')) return
    try {
      await pb.collection(COL_ROLES).delete(id)
      await load()
    } catch (e: unknown) {
      alert(pocketbaseErrorMessage(e, 'Ошибка удаления'))
    }
  }

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    return items.filter((row) => {
      const perms = normalizePermissions(row.permissions)
      const permText = perms.map(permissionLabel).join(' ')
      return matchesListSearch(searchQuery, row.name, row.title, perms.join(' '), permText)
    })
  }, [items, searchQuery])

  return (
    <PageShell>
      <PageHeader
        title="Роли"
        lead={
          <>
            Права доступа в приложении. Код <code>name</code> синхронизируется с CRM; назначение прав — здесь.
          </>
        }
      />
      {err && <Alert kind="err">{err}</Alert>}
      <div className={toolbarClass}>
        {can(PERMISSION_KEYS.ROLES_WRITE) && (
          <ButtonLink to="/admin/administration/roles/new" variant="primary">
            Добавить роль
          </ButtonLink>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по коду, названию, правам…"
            aria-label="Поиск ролей"
          />
        </div>
      </div>
      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Код (name)</th>
              <th className={tableHeadClass}>Название</th>
              <th className={tableHeadClass}>Права</th>
              <th className={tableHeadClass} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={4}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && displayedItems.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={4}>
                  <em>
                    {items.length === 0
                      ? 'Нет ролей — создайте первую или дождитесь синхронизации при входе из CRM.'
                      : 'Ничего не найдено'}
                  </em>
                </td>
              </tr>
            )}
            {!loading &&
              displayedItems.map((row) => (
                <tr key={row.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>
                    <code>{row.name}</code>
                  </td>
                  <td className={tableCellClass}>{row.title}</td>
                  <td className={cn(tableCellClass, 'max-w-[360px]')}>
                    {formatPermissionsDisplay(row.permissions) || '—'}
                  </td>
                  <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                    {can(PERMISSION_KEYS.ROLES_WRITE) && (
                      <Link to={`/admin/administration/roles/${row.id}`} className={btn('ghost')}>
                        Изменить
                      </Link>
                    )}
                    {can(PERMISSION_KEYS.ROLES_DELETE) && (
                      <button
                        type="button"
                        className={cn(btn('danger'), 'ml-1')}
                        onClick={() => void removeRow(row.id)}
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  )
}
